"use client"

import {
  NotAppliedError,
  UnauthenticatedError,
  fetchDrops,
  fetchOptInStatus,
  fetchStats,
} from "@src/features/promotions/lib/pointsAPI"
import { useQuery } from "@tanstack/react-query"

// Polling cadences from the guide §14. Reads fail soft — keep last-known data.
// Stop polling while a query is stuck erroring (resumes on recovery) so a down
// endpoint isn't hit forever.
const POLL_MS = 60_000

export function useDrops() {
  return useQuery({
    queryKey: ["promotions", "drops"],
    queryFn: ({ signal }) => fetchDrops(signal),
    staleTime: 60_000,
    refetchInterval: (query) =>
      query.state.status === "error" ? false : POLL_MS,
  })
}

// Scoped to the account so a same-tab wallet switch can't leak A's stats to B.
// 401/404 are expected states, not failures — don't retry them.
export function useOptInStatus(accountId: string | undefined) {
  return useQuery({
    queryKey: ["promotions", "opt-in-status", accountId],
    queryFn: () => fetchOptInStatus(),
    enabled: Boolean(accountId),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      if (error instanceof UnauthenticatedError) return false
      return failureCount < 2
    },
  })
}

export function useStats(
  accountId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: ["promotions", "stats", accountId],
    queryFn: ({ signal }) => fetchStats(signal),
    enabled: Boolean(accountId) && enabled,
    staleTime: 60_000,
    refetchInterval: (query) => {
      // A newly opted-in account can legitimately have no Points row until the
      // next hourly ingestion. Keep checking that expected pending state, while
      // still stopping on authentication or operational failures.
      if (query.state.error instanceof NotAppliedError) return POLL_MS
      return query.state.status === "error" ? false : POLL_MS
    },
    refetchOnWindowFocus: true,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === accountId ? previousData : undefined,
    retry: (failureCount, error) => {
      if (
        error instanceof NotAppliedError ||
        error instanceof UnauthenticatedError
      ) {
        return false
      }
      return failureCount < 2
    },
  })
}
